import {ElementNode, NodeKey, ElementFormatType} from "lexical";
import {SerializedImageNode} from "./image-node";

export type SerializedLoadingImageNode = {
    src: string;
    altText: string;
    type: "loading-image";
    version: 1;
    fileId: string;
    originalName: string;
    children: any[];
    direction: "ltr" | "rtl" | null;
    format: ElementFormatType;
    indent: number;
};

export class LoadingImageNode extends ElementNode {
    __src: string;
    __altText: string;
    __fileId: string;
    __originalName: string;

    static getType(): string {
        return "loading-image";
    }

    static clone(node: LoadingImageNode): LoadingImageNode {
        return new LoadingImageNode(
            node.__src, 
            node.__altText, 
            node.__fileId,
            node.__originalName,
            node.__key
        );
    }

    constructor(
        src = "", 
        altText = "", 
        fileId = "",
        originalName = "",
        key?: NodeKey
    ) {
        super(key);
        this.__src = src;
        this.__altText = altText;
        this.__fileId = fileId;
        this.__originalName = originalName;
    }

    static importJSON(serializedNode: SerializedLoadingImageNode): LoadingImageNode {
        const {src, altText, fileId, originalName} = serializedNode;
        return new LoadingImageNode(src, altText, fileId, originalName);
    }

    exportJSON(): SerializedLoadingImageNode {
        return {
            src: this.__src,
            altText: this.__altText,
            type: "loading-image",
            version: 1,
            fileId: this.__fileId,
            originalName: this.__originalName,
            children: [],
            direction: null,
            format: "" as ElementFormatType,
            indent: 0
        };
    }

    createDOM(): HTMLElement {
        const container = document.createElement("div");
        container.className = "loading-image-container";
        container.setAttribute("contenteditable", "false");
        
        // Rzeczywisty obraz z base64
        const image = document.createElement("img");
        image.src = this.__src;
        image.alt = this.__altText;
        image.className = "max-w-full h-auto max-h-[300px] max-w-[300px]";
        
        // Nakładka loading
        const overlay = document.createElement("div");
        overlay.className = "absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center";
        overlay.innerHTML = `
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></div>
            <div class="text-white text-sm mt-1 font-semibold">Uploadowanie...</div>
            <div class="text-white text-xs">${this.__originalName}</div>
        `;
        
        container.appendChild(image);
        container.appendChild(overlay);
        
        return container;
    }

    updateDOM(): false {
        return false;
    }

    getFileId(): string {
        return this.__fileId;
    }

    updateImage(newSrc: string): void {
        const writable = this.getWritable();
        writable.__src = newSrc;
    }

    convertToImageNode(id: string): SerializedImageNode {
        return {
            id,
            src: this.__src,
            altText: this.__altText,
            type: "image",
            version: 1,
        };
    }

    static create(payload: {
        src: string;
        altText: string;
        fileId: string;
        originalName: string;
    }): LoadingImageNode {
        return $createLoadingImageNode(payload);
    }
}

export function $createLoadingImageNode({
    src, 
    altText, 
    fileId, 
    originalName
}: { 
    src: string; 
    altText: string; 
    fileId: string;
    originalName: string;
}): LoadingImageNode {
    return new LoadingImageNode(src, altText, fileId, originalName);
}
